// src/App.jsx
import { createPortal, lazy, Suspense } from '@wordpress/element';
import { ErrorBoundary } from '@sentry/react';
import { Helmet } from 'react-helmet';

import Shortcode from './Shortcode';
import LoadingSpinner from './components/LoadingSpinner';
import ErrorComponent from './ErrorComponent';
import Fallback from './Fallback.jsx';

import { useConfig } from './contexts/ConfigContext';
import { useAppState } from './contexts/AppStateContext';
import { useCart } from './contexts/CartContext';
import { useRetailerData } from './hooks/dutchie-queries.js';
import { useInitialURLParams } from './hooks/useInitialURLParams';

const CartSidebarIcon = lazy(() => import('./components/Cart/CartSidebarIcon'));
const CartSidebarWrapper = lazy(() => import('./components/Cart/CartSidebarWrapper'));
const LocationModal = lazy(() => import('./components/LocationSelect/LocationModal'));

const App = ({ fallback }) => {
    const initialParams = useInitialURLParams();

    // Get all needed values from contexts
    const {
        retailers,
        nonHeadlessRetailers,
        shortcodeData,
    } = useConfig();

    const {
        isInitialized,
        isAppReady,
        retailerId,
        setRetailerId,
        currentRetailer,
        nonHeadlessRetailer,
        setNonHeadlessRetailer,
        nonHeadlessRetailerData,
        setNonHeadlessRetailerData,
        fallbackEnabled,
        gatesPassed,
        needsLocationGate,
        needsAgeGate,
        passAgeGate,
        shouldRenderCart,
    } = useAppState();

    const { isFetched: isFetchedCartData } = useCart();
    const { isFetched: isFetchedRetailerData } = useRetailerData(retailerId);

    // Simple state checks - no complex logic
    if (!isInitialized) {
        return <LoadingSpinner />;
    }

    // Fallback mode
    if (fallbackEnabled && retailerId) {
        return (
            <>
                {Object.entries(shortcodeData).map(([rootId, dataset], i) => {
                    const root = document.getElementById(rootId);
                    const fallbackElement = (
                        <Fallback
                            key={rootId}
                            rootId={rootId}
                            shortcodeData={dataset}
                            script={fallback[retailerId].script}
                        />
                    );

                    return i === 0 ? fallbackElement : createPortal(fallbackElement, root);
                })}
            </>
        );
    }

    // Gates not passed
    if (!gatesPassed && !nonHeadlessRetailer) {
        return createPortal(
            <ErrorBoundary fallback={(props) => <ErrorComponent {...props} />}>
                <Suspense fallback={<LoadingSpinner />}>
                    <Helmet>
                        <link rel="canonical" href={window.location.href} />
                    </Helmet>
                    <LocationModal
                        retailers={retailers}
                        retailerId={retailerId}
                        onRetailerSelect={setRetailerId}
                        onAgeGatePass={passAgeGate}
                        showLocationGate={needsLocationGate}
                        showAgeGate={needsAgeGate && !needsLocationGate}
                        nonHeadlessRetailer={nonHeadlessRetailer}
                        setNonHeadlessRetailer={setNonHeadlessRetailer}
                        nonHeadlessRetailerData={nonHeadlessRetailerData}
                        setNonHeadlessRetailerData={setNonHeadlessRetailerData}
                    />
                </Suspense>
            </ErrorBoundary>,
            document.body
        );
    }

    // Waiting for data
    if (!isAppReady || (!isFetchedRetailerData && retailerId) || (!isFetchedCartData && retailerId)) {
        return <LoadingSpinner />;
    }

    // Main app render - just display components
    return (
        <>
            <Helmet>
                <link rel="canonical" href={window.location.href} />
            </Helmet>
            <ErrorBoundary fallback={(props) => <ErrorComponent {...props} />}>
                {/* Render shortcodes */}
                {Object.entries(shortcodeData).map(([rootId, dataset], i) => {
                    const root = document.getElementById(rootId);
                    const shortcodeElement = (
                        <Shortcode
                            key={rootId}
                            rootId={rootId}
                            shortcodeData={dataset}
                            forcedOrderType={initialParams}
                        />
                    );

                    return i === 0 ? shortcodeElement : createPortal(shortcodeElement, root);
                })}

                {/* Render cart sidebar if needed */}
                {shouldRenderCart && createPortal(
                    <ErrorBoundary fallback={(props) => <ErrorComponent {...props} />}>
                        <Suspense fallback={<LoadingSpinner />}>
                            <CartSidebarIcon fixedCart={true} />
                            <CartSidebarWrapper fixedCart={true} forcedOrderType={initialParams} />
                        </Suspense>
                    </ErrorBoundary>,
                    document.body
                )}
            </ErrorBoundary>
        </>
    );
};

export default App;
